import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Rating,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { useLocation } from "react-router-dom";

import Client from "../components/Client";
import Config from "../components/ClientConfig";
import { useDataStore } from "../dataStore";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const MAX_DETAILS_LENGTH = 512;
const pageShellSx = {
  px: { xs: 0, sm: 2 },
  py: { xs: 0.5, sm: 2.5 },
  display: "flex",
  justifyContent: "center",
};
const pageSurfaceSx = {
  borderRadius: "8px",
  overflow: "hidden",
  border: "1px solid rgba(124,45,18,0.10)",
  bgcolor: "rgba(255,255,255,0.94)",
  boxShadow: "0 20px 48px rgba(33,20,12,0.10)",
};
const headerSx = {
  p: { xs: 1.5, sm: 2.5, md: 3 },
  color: "#431407",
  background:
    "linear-gradient(135deg, rgba(255,250,243,0.98) 0%, rgba(255,237,213,0.96) 54%, rgba(254,215,170,0.88) 100%)",
  borderBottom: "1px solid rgba(124,45,18,0.10)",
};
const headerIconSx = {
  width: 44,
  height: 44,
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#111111",
  bgcolor: "#F7B500",
  border: "1px solid rgba(255,255,255,0.20)",
  flexShrink: 0,
};
const stepCardSx = {
  borderRadius: "8px",
  borderColor: "rgba(124,45,18,0.10)",
  bgcolor: "rgba(255,255,255,0.92)",
};
const primaryButtonSx = {
  borderRadius: "8px",
  fontWeight: 900,
  textTransform: "none",
  boxShadow: "none",
};
const secondaryButtonSx = {
  borderRadius: "8px",
  fontWeight: 850,
  textTransform: "none",
};

export default function FeedbackGenerator() {
  const query = useQuery();
  const { languageCode } = useDataStore();

  const TEXT = useMemo(
    () => ({
      ro: {
        title: "Feedback",
        subtitle: "Validează comanda, apoi lasă un feedback",
        validated: "Validat",
        notValidated: "Nevalidat",

        clear: "Șterge",
        validate: "Validează",
        working: "Se procesează…",

        step1Title: "Validare comandă",
        step1Desc: "Introdu codul, suma și data comenzii.",

        orderCode: "Cod comandă",
        amount: "Sumă",
        orderDate: "Data comenzii",

        step1InfoValidated: "Comanda a fost validată. Poți trimite feedback acum.",
        step1InfoAlready: "Feedback-ul a fost deja trimis pentru această comandă.",
        okThanks: "Mulțumim! Feedback-ul a fost trimis.",

        step2Title: "Feedback",
        score: "Scor",
        details: "Mesaj",
        detailsPlaceholder: "Scrie aici experiența ta...",
        submit: "Trimite feedback",

        shareContact: "Vreau să las date de contact",
        shareContactHelp: "Opțional. Le vom folosi doar dacă este nevoie să te contactăm pentru acest feedback.",
        name: "Nume",
        email: "Email",
        optional: "opțional",

        sendAnother: "Trimite alt feedback",
        validateAnotherOrder: "Validează altă comandă",

        errValidate: "Nu s-a putut valida comanda. Verifică codul, suma și data.",
        errSubmit: "Nu s-a putut trimite feedback-ul. Verifică și încearcă din nou.",
        errFill: "Completează scorul și mesajul.",
        errEmail: "Adresa de email nu este validă.",
      },

      en: {
        title: "Feedback",
        subtitle: "Validate order, then leave feedback",
        validated: "Validated",
        notValidated: "Not validated",

        clear: "Clear",
        validate: "Validate",
        working: "Working…",

        step1Title: "Validate order",
        step1Desc: "Enter order code, amount and order date.",

        orderCode: "Order code",
        amount: "Amount",
        orderDate: "Order date",

        step1InfoValidated: "Order validated. You can submit feedback now.",
        step1InfoAlready: "Feedback was already submitted for this order.",
        okThanks: "Thanks! Your feedback was submitted.",

        step2Title: "Feedback",
        score: "Score",
        details: "Message",
        detailsPlaceholder: "Write your experience here...",
        submit: "Submit feedback",

        shareContact: "I want to share contact details",
        shareContactHelp: "Optional. We will only use them if we need to follow up about this feedback.",
        name: "Name",
        email: "Email",
        optional: "optional",

        sendAnother: "Send another feedback",
        validateAnotherOrder: "Validate another order",

        errValidate: "Could not validate order. Please check code, amount and date.",
        errSubmit: "Could not submit feedback. Please try again.",
        errFill: "Fill score and message.",
        errEmail: "Email address is not valid.",
      },
    }),
    []
  );

  const lang = String(languageCode || "RO").toLowerCase() === "en" ? "en" : "ro";
  const t = TEXT[lang] || TEXT.ro;
  const autoValidatedRef = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;

    const originalConsoleError = console.error;
    console.error = (...args) => {
      const msg = args?.[0]?.toString?.() || "";
      if (msg.includes("ResizeObserver loop completed with undelivered notifications")) return;
      originalConsoleError(...args);
    };

    return () => {
      console.error = originalConsoleError;
    };
  }, []);

  const clientRef = useRef(null);
  if (!clientRef.current) clientRef.current = new Client();
  const client = clientRef.current;

  const [orderCode, setOrderCode] = useState("");
  const [amount, setAmount] = useState("");
  const [orderDate, setOrderDate] = useState("");

  const [identified, setIdentified] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [submittedOk, setSubmittedOk] = useState(false);

  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState(null);

  const [score, setScore] = useState("5");
  const [details, setDetails] = useState("");

  const [shareContact, setShareContact] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  const resetFeedbackFields = () => {
    setScore("5");
    setDetails("");
    setShareContact(false);
    setCustomerName("");
    setCustomerEmail("");
  };

  const canValidate = Boolean(orderCode.trim()) && Boolean(amount.trim()) && Boolean(orderDate.trim());
  const scoreNum = Number(score);

  const emailIsValid =
    !shareContact ||
    !customerEmail.trim() ||
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim());

  const canSubmit =
    identified &&
    !alreadySubmitted &&
    !submittedOk &&
    scoreNum >= 1 &&
    scoreNum <= 5 &&
    details.trim().length >= 2 &&
    details.trim().length <= MAX_DETAILS_LENGTH &&
    emailIsValid;

  function extract(res) {
    const ok = String(res?.status || "").toUpperCase() === "OK";
    const data = res?.data || {};
    const message = String(res?.message || "");
    return { ok, data, message };
  }

  function validateFeedbackPublic(payload) {
    const url = Config.API_URL_ORDERS + "feedback_validate/";
    return client.pushData(url, payload);
  }

  function submitFeedbackPublic(payload) {
    const url = Config.API_URL_ORDERS + "feedback_submit/";
    return client.pushData(url, payload);
  }

  function clearAll() {
    autoValidatedRef.current = false;
    setOrderCode("");
    setAmount("");
    setOrderDate("");

    setIdentified(false);
    setAlreadySubmitted(false);
    setSubmittedOk(false);

    resetFeedbackFields();
    setBanner(null);
  }

  function clearValidationOnly() {
    setIdentified(false);
    setAlreadySubmitted(false);
    setSubmittedOk(false);
    setBanner(null);
  }

  function buildFinalDetails() {
    const message = (details || "")
      .trim()
      .replace(/\n{3,}/g, "\n\n"); // max 2 empty lines
    const contactParts = [
      shareContact && customerName.trim() ? customerName.trim() : null,
      shareContact && customerEmail.trim() ? customerEmail.trim() : null,
    ].filter(Boolean);
    if (contactParts.length === 0) return message;

    return `${message}\n— Contact: ${contactParts.join(" | ")}`;
  }

  useEffect(() => {
    const qCode = query.get("code") || query.get("orderCode");
    const qAmount = query.get("amount") || query.get("totalAmount") || query.get("sum") || query.get("total");
    const qDate = query.get("date") || query.get("orderDate");

    const nextCode = (orderCode || qCode || "").trim();
    const nextAmount = (amount || qAmount || "").trim();
    const nextDate = (orderDate || qDate || "").trim();

    if (qCode && !orderCode) setOrderCode(qCode);
    if (qAmount && !amount) setAmount(qAmount);
    if (qDate && !orderDate) setOrderDate(qDate);

    const hasAll =
      Boolean((qCode || orderCode)?.trim()) &&
      Boolean((qAmount || amount)?.trim()) &&
      Boolean((qDate || orderDate)?.trim());

    if (hasAll && !autoValidatedRef.current) {
      autoValidatedRef.current = true;

      (async () => {
        resetFeedbackFields();
        setIdentified(false);
        setAlreadySubmitted(false);
        setSubmittedOk(false);
        setBanner(null);

        setLoading(true);
        try {
          const payload = {
            order: {
              code: nextCode,
              totalAmount: nextAmount,
              orderDate: nextDate,
            },
          };

          const res = await validateFeedbackPublic(payload);
          const { ok, data, message } = extract(res);

          if (!ok) {
            setBanner({ type: "error", text: message || t.errValidate });
            return;
          }

          const isIdentified = Boolean(data?.identified ?? true);
          const isAlready = Boolean(data?.already_submitted ?? false);

          setIdentified(isIdentified);
          setAlreadySubmitted(isAlready);

          if (data?.feedback) {
            if (data.feedback.score !== undefined && data.feedback.score !== null) {
              setScore(String(data.feedback.score));
            }
            if (data.feedback.details) {
              setDetails(String(data.feedback.details));
            }
          }

          setBanner({
            type: "success",
            text: message || (isAlready ? t.step1InfoAlready : t.step1InfoValidated),
          });
        } catch (_) {
          setBanner({ type: "error", text: t.errValidate });
        } finally {
          setLoading(false);
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function onValidateOrder() {
    resetFeedbackFields();

    setIdentified(false);
    setAlreadySubmitted(false);
    setSubmittedOk(false);
    setBanner(null);

    if (!canValidate) return;

    setLoading(true);
    try {
      const payload = {
        order: {
          code: orderCode.trim(),
          totalAmount: amount.trim(),
          orderDate: orderDate.trim(),
        },
      };

      const res = await validateFeedbackPublic(payload);
      const { ok, data, message } = extract(res);

      if (!ok) {
        setBanner({ type: "error", text: message || t.errValidate });
        return;
      }

      const isIdentified = Boolean(data?.identified ?? true);
      const isAlready = Boolean(data?.already_submitted ?? false);

      setIdentified(isIdentified);
      setAlreadySubmitted(isAlready);

      if (data?.feedback) {
        if (data.feedback.score !== undefined && data.feedback.score !== null) {
          setScore(String(data.feedback.score));
        }
        if (data.feedback.details) {
          setDetails(String(data.feedback.details));
        }
      }

      setBanner({
        type: "success",
        text: message || (isAlready ? t.step1InfoAlready : t.step1InfoValidated),
      });
    } catch (_) {
      setBanner({ type: "error", text: t.errValidate });
    } finally {
      setLoading(false);
    }
  }

  async function onSubmitFeedback() {
    setBanner(null);

    if (!identified) return;

    if (!emailIsValid) {
      setBanner({ type: "error", text: t.errEmail });
      return;
    }

    if (!canSubmit) {
      setBanner({ type: "error", text: t.errFill });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        order: {
          code: orderCode.trim(),
          totalAmount: amount.trim(),
          orderDate: orderDate.trim(),
        },
        score: Math.round(scoreNum),
        feedback: buildFinalDetails(),
      };

      const res = await submitFeedbackPublic(payload);
      const { ok, message } = extract(res);

      if (!ok) {
        setBanner({ type: "error", text: message || t.errSubmit });
        return;
      }

      setSubmittedOk(true);
      setAlreadySubmitted(true);
      resetFeedbackFields();

      setBanner({ type: "success", text: message || t.okThanks });
    } catch (_) {
      setBanner({ type: "error", text: t.errSubmit });
    } finally {
      setLoading(false);
    }
  }

  const showValidateSection = !submittedOk && !identified;
  const showFeedbackSection = !submittedOk && identified;
  const showFinalSuccessOnly = submittedOk;

  return (
    <Box sx={pageShellSx}>
      <Box sx={{ width: "100%", maxWidth: 1040 }}>
        <Box sx={pageSurfaceSx}>
          <Box sx={headerSx}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={headerIconSx}
              >
                <RateReviewOutlinedIcon />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 950, lineHeight: 1.1 }}>
                  {t.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(67,20,7,0.68)",
                    display: { xs: "none", sm: "block" },
                    mt: 0.35,
                  }}
                >
                  {t.subtitle}
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }} />

              {identified || submittedOk ? (
                <Chip
                  size="small"
                  icon={<CheckCircleOutlineOutlinedIcon />}
                  label={t.validated}
                  sx={{
                    fontWeight: 850,
                    color: "#111111",
                    bgcolor: "#F7B500",
                    "& .MuiChip-icon": { color: "#111111" },
                  }}
                />
              ) : (
                <Chip
                  size="small"
                  icon={<ErrorOutlineOutlinedIcon />}
                  label={t.notValidated}
                  variant="outlined"
                  sx={{
                    fontWeight: 850,
                    color: "#7c2d12",
                    borderColor: "rgba(124,45,18,0.26)",
                    bgcolor: "rgba(255,255,255,0.62)",
                    "& .MuiChip-icon": { color: "#9a3412" },
                  }}
                />
              )}

            </Stack>
          </Box>

          <Box sx={{ p: { xs: 1.25, sm: 2, md: 2.5 } }}>
            {banner ? (
              <Alert severity={banner.type} sx={{ mb: 1.25, borderRadius: "8px" }}>
                {banner.text}
              </Alert>
            ) : null}

            {showFinalSuccessOnly ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
                <Typography sx={{ fontWeight: 800 }}>{banner?.text || t.okThanks}</Typography>
                <Button variant="outlined" onClick={clearAll} disabled={loading} sx={secondaryButtonSx}>
                  {t.sendAnother}
                </Button>
              </Stack>
            ) : null}

            {showValidateSection ? (
              <Card variant="outlined" sx={stepCardSx}>
                <CardContent sx={{ p: { xs: 1.25, sm: 2 } }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900 }}>{t.step1Title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t.step1Desc}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1 }} />

                    <Stack direction="row" spacing={1} sx={{ justifyContent: { xs: "flex-end", sm: "initial" } }}>
                      <Button variant="text" onClick={clearAll} disabled={loading} sx={secondaryButtonSx}>
                        {t.clear}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={onValidateOrder}
                        disabled={!canValidate || loading}
                        sx={primaryButtonSx}
                      >
                        {loading ? t.working : t.validate}
                      </Button>
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  <Grid container spacing={1.25}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t.orderCode}
                        value={orderCode}
                        onChange={(e) => {
                          setOrderCode(e.target.value);
                          clearValidationOnly();
                        }}
                        fullWidth
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label={t.amount}
                        value={amount}
                        onChange={(e) => {
                          setAmount(e.target.value);
                          clearValidationOnly();
                        }}
                        fullWidth
                        size="small"
                        inputProps={{ style: { textAlign: "right" } }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label={t.orderDate}
                        type="date"
                        value={orderDate}
                        onChange={(e) => {
                          setOrderDate(e.target.value);
                          clearValidationOnly();
                        }}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ) : null}

            {showFeedbackSection ? (
              <Card variant="outlined" sx={stepCardSx}>
                <CardContent sx={{ p: { xs: 1.25, sm: 2 } }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography sx={{ fontWeight: 900 }}>{t.step2Title}</Typography>
                    <Button variant="outlined" onClick={clearAll} disabled={loading} sx={secondaryButtonSx}>
                      {t.validateAnotherOrder}
                    </Button>
                  </Stack>

                  {alreadySubmitted ? (
                    <Stack spacing={1}>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {t.score}
                        </Typography>
                        <Rating value={Number(score) || 0} readOnly />
                        <Typography variant="caption" color="text.secondary">
                          {score ? `${score}/5` : ""}
                        </Typography>
                      </Stack>

                      <TextField
                        label={t.details}
                        value={details}
                        fullWidth
                        size="small"
                        multiline
                        minRows={4}
                        InputProps={{ readOnly: true }}
                      />

                      <Button variant="outlined" onClick={clearAll} disabled={loading} sx={secondaryButtonSx}>
                        {t.sendAnother}
                      </Button>
                    </Stack>
                  ) : (
                    <Grid container spacing={1.25}>
                      <Grid item xs={12} sm={3}>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {t.score}
                          </Typography>
                          <Rating value={Number(score) || 0} onChange={(_, newValue) => setScore(String(newValue || ""))} />
                          <Typography variant="caption" color="text.secondary">
                            {score ? `${score}/5` : ""}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} sm={9}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={shareContact}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setShareContact(checked);

                                if (!checked) {
                                  setCustomerName("");
                                  setCustomerEmail("");
                                }
                              }}
                            />
                          }
                          label={t.shareContact}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: { sm: 5 } }}>
                          {t.shareContactHelp}
                        </Typography>
                      </Grid>

                      {shareContact ? (
                        <>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label={`${t.name} (${t.optional})`}
                              value={customerName}
                              onChange={(e) => setCustomerName(e.target.value)}
                              fullWidth
                              size="small"
                              inputProps={{ maxLength: 120 }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label={`${t.email} (${t.optional})`}
                              value={customerEmail}
                              onChange={(e) => setCustomerEmail(e.target.value)}
                              fullWidth
                              size="small"
                              type="email"
                              error={Boolean(customerEmail.trim()) && !emailIsValid}
                              helperText={Boolean(customerEmail.trim()) && !emailIsValid ? t.errEmail : " "}
                              inputProps={{ maxLength: 160 }}
                            />
                          </Grid>
                        </>
                      ) : null}

                      <Grid item xs={12}>
                        <TextField
                          label={t.details}
                          placeholder={t.detailsPlaceholder}
                          value={details}
                          onChange={(e) => setDetails(e.target.value.slice(0, MAX_DETAILS_LENGTH))}
                          fullWidth
                          size="small"
                          multiline
                          minRows={4}
                          maxRows={8}
                          helperText={`${details.length}/${MAX_DETAILS_LENGTH}`}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          disabled={!canSubmit || loading}
                          onClick={onSubmitFeedback}
                          sx={primaryButtonSx}
                        >
                          {loading ? t.working : t.submit}
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </CardContent>
              </Card>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
